import { ArrowRightIcon, BadgeCheckIcon, BanIcon, CheckCircleIcon, ChevronDownIcon, ClipboardCopyIcon, ClockIcon, DocumentSearchIcon, DocumentTextIcon, DotsCircleHorizontalIcon, DotsHorizontalIcon, DotsVerticalIcon, ExclamationCircleIcon, ExclamationIcon, ExternalLinkIcon, EyeIcon, LinkIcon, MailIcon, MailOpenIcon, RefreshIcon, TrashIcon, XCircleIcon } from "@heroicons/react/outline";
import { TrackingPixelEvent, WorkflowAutomationItem } from "../interfaces";
import { BLOCK_ICON_MAP, MIMETYPE_ICON_MAP } from "../utils";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { WorkflowContext, useModal } from "../contexts";
import { OAuthError, deleteData, postData } from "../backend";
import { Popover, Transition } from "@headlessui/react";
import OAuthModal from "./OAuthModal";
import CopyToClipboard from "react-copy-to-clipboard";
import { Link, useHistory } from "react-router-dom";
import useUser from "../hooks/useUser";
import ErrorModal from "./ErrorModal";
import { useTranslation } from "react-i18next";
import HeroIcon from "./HeroIcon";
import WorkflowOutputsItemFile from "./WorkflowOutputsItemFile";

interface Props {
  item: WorkflowAutomationItem;
  lastViewedTime: number;
  hidePreview?: boolean;
  showWorkflow?: boolean;
}

function WorkflowOutputsItem(props: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [resendStatus, setResendStatus] = useState<"INITIAL" | "SENDING" | "RESENT" | "ERROR">("INITIAL");
  const { t } = useTranslation();

  const { hasPermission } = useUser();

  const history = useHistory();
  const { user } = useUser();

  const [actionStatus, setActionStatus] = useState<"INITIAL" | "DELETED" | "RERUNNING">("INITIAL");

  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [copiedSignatureRequest, setCopiedSignatureRequest] = useState<number>();

  const [item, setItem] = useState(props.item)
  const { outputs, outputEmails, signatureRequests, approvalRequests } = item;

  const { openModal } = useModal();

  const documentOutputs = item.byServiceAccount ? [] : outputs.filter(output => !!output.file);
  const pdfOuputs = item.byServiceAccount ? [] : outputs.filter(output => (!!output.filePdf));
  const microsoftOuputs = item.byServiceAccount ? [] : outputs.filter(output => (!!output.fileMicrosoft));
  const onedriveOuputs = outputs.filter(output => (!!output.onedriveFile));
  const onedrivePdfs = outputs.filter(output => (!!output.onedrivePdf));
  const hubspotPdfs = outputs.filter(output => (!!output.hubspotFilePdf));
  const hubspotMicrosftFiles = outputs.filter(output => (!!output.hubspotFileMicrosoft));

  const { workflow: currentWorkflow } = useContext(WorkflowContext);

  // const allTrackingPixels: Array<TrackingPixel> = [...item.signatureRequests.map(s => s.trackingPixel), ...item.outputEmails.map(e => e.trackingPixel)].filter(Boolean);
  // const allTrackingPixelEvents: Array<TrackingPixelEvent> = allTrackingPixels.map(p => p.events).flat();

  useEffect(() => setItem(props.item), [props.item]);

  function handleResendSignatureRequest(id: string) {
    setResendStatus("SENDING");

    postData(`/signatures/resend/${id}/`)
      .then(() => setResendStatus("RESENT"))
      .catch(() => setResendStatus("ERROR"))
  }

  function handleDeleteItem() {
    setActionStatus("DELETED");
    deleteData(`/workflows/automation-items/${item.id}/`)
      .catch(() => setActionStatus("INITIAL"));
  }

  function handleRerunItem() {
    setActionStatus("RERUNNING");
    postData(`/workflows/automation-items/${item.id}/rerun/`)
      .catch(error => {
        setActionStatus("INITIAL");
        if (error instanceof OAuthError)
          openModal(<OAuthModal oauthUrl={error.oauthUrl} />);
        else
          openModal(<ErrorModal details={error} />);
      });
  }

  function handleRescindItem() {
    setActionStatus("RERUNNING");

    postData(`/workflows/automation-items/${item.id}/rescind/`)
      .then(setItem)
      .catch(console.error)
      .finally(() => setActionStatus("INITIAL"));
  }

  function handleOnCopySupport() {
    setShowCopiedMessage(true);
    setTimeout(() => setShowCopiedMessage(false), 2000);
  }

  function handleOnCopiedSignatureRequest(recipient: number) {
    setCopiedSignatureRequest(recipient);
    setTimeout(() => setCopiedSignatureRequest(undefined), 2000);
  }

  const itemOutputs: Array<{ name?: string, icon: string } | undefined> = [
    ...(item.byServiceAccount ? [] : outputs).map(output => ({ name: output.file?.name, icon: MIMETYPE_ICON_MAP[output.file?.mimeType ?? "application/vnd.google-apps.document"] })),
    ...(outputs).map(output => ({ name: output.onedriveFile?.fileName, icon: MIMETYPE_ICON_MAP[output.onedriveFile?.fileMimeType ?? "application/vnd.google-apps.file"] })),
    ...(item.byServiceAccount ? [] : outputs).map(output => ({ name: output.filePdf?.name, icon: MIMETYPE_ICON_MAP["application/pdf"] })),
    ...(item.byServiceAccount ? [] : outputs).map(output => ({ name: output.fileMicrosoft?.name, icon: MIMETYPE_ICON_MAP[output.fileMicrosoft?.mimeType ?? "application/vnd.google-apps.file"] })),
    ...(outputs).map(output => ({ name: output.onedrivePdf?.fileName, icon: MIMETYPE_ICON_MAP["application/pdf"] })),
    ...outputEmails.map(email => ({ name: email.emailSubject, icon: BLOCK_ICON_MAP[email.emailType] }))
  ].filter(item => !!item.name);

  const [primaryOutput, ...remainingOutputs] = itemOutputs;

  return (
    <>
      <div className={`flex items-center text-sm font-semibold font-gilroy text-gray-600 cursor-pointer h-14 last:border-b-0 ${!isExpanded && "border-b border-gray-200"}`} onClick={(e) => { setIsExpanded(f => !f); e.stopPropagation(); }}>

        {/* Indicator */}
        <div className="flex items-center justify-center w-2 flex-shrink-0">
          {props.lastViewedTime < new Date(item.updatedAt).getTime() &&
            <div className="bg-green w-2 h-2 rounded-full" />
          }
        </div>

        {/* Expand chevron */}
        <div className="flex items-center justify-center w-8 flex-shrink-0">
          <button onClick={(e) => { setIsExpanded(f => !f); e.stopPropagation(); }}>
            <ChevronDownIcon className={`w-6 h-6 text-gray-400 cursor-pointer transform transition-all ${isExpanded ? "rotate-0" : "-rotate-90"}`} />
          </button>
        </div>

        {/* Filename */}
        <div className="flex items-center gap-2 group w-full min-w-[400px]">
          {(!primaryOutput && item.status === 'PENDING') &&
            <div className="text-gray-500 font-semibold">
              {t("In progress...")}
            </div>
          }

          {(!primaryOutput && item.status !== 'PENDING') &&
            <div className="text-yellow font-semibold flex items-center gap-4">
              {t("No outputs or emails were created for this automation item")}
              {item.errorReason &&
                <div className="underline text-gray-600">View error details</div>
              }
            </div>
          }

          {primaryOutput &&
            <>
              <img src={primaryOutput.icon} className="w-4 h-4" alt="Output Icon" />
              <div className={`max-w-[400px] truncate ${actionStatus !== "INITIAL" ? "text-gray-400 line-through" : "text-gray-600"}`}>
                {primaryOutput.name}
              </div>
            </>
          }

          {remainingOutputs.length > 0 &&
            <div className="ml-1 text-gray-400">+</div>
          }

          {remainingOutputs.map((output, index) => <img key={index} src={output?.icon} className="w-4 h-4" alt="Output Icon" />)}
        </div>

        {/* Tracking */}
        {/* <div className="h-14 w-20">
          {(allTrackingPixels.length > 0 && allTrackingPixelEvents.length === 0) &&
            <div className="relative group cursor-default">
              <EyeIcon className="w-6 h-6 text-gray-400 mx-auto" />
              <div className="hidden group-hover:block absolute bg-white shadow-lg text-center w-72 -right-32 top-6 z-10 p-2 rounded border border-gray-200">
                {t("No views yet")}
              </div>
            </div>
          }

          {(allTrackingPixels.length > 0 && allTrackingPixelEvents.length > 0) &&
            <div className="relative group cursor-default">
              <EyeIcon className="w-6 h-6 text-blue mx-auto" />
              <div className="hidden group-hover:block absolute bg-white shadow-lg text-center w-72 -right-32 top-6 z-10 p-2 rounded border border-gray-200">
                <span className="font-semibold">{allTrackingPixelEvents.length}</span>{allTrackingPixelEvents.length >= 2 ? t("views") : t("view")}, last viewed {moment(allTrackingPixelEvents[0].createdAt).fromNow()}
              </div>
            </div>
          }
        </div> */}


        {/* Workflow column */}
        {props.showWorkflow &&
          <Link to={`/w/${item.workflow.id}/outputs`} className="flex items-center w-60 flex-shrink-0 gap-2 group">
            <div className="flex items-center justify-center w-6 h-6 text-white rounded cursor-pointer" style={{ backgroundColor: item.workflow.color }}>
              <HeroIcon icon={item.workflow.icon} className="w-4 h-4" />
            </div>
            <h3 className="font-semibold font-gilroy truncate text-gray-600 cursor-pointer group-hover:underline">
              {item.workflow.name}
            </h3>
          </Link>
        }

        {/* Running user */}
        {(!props.showWorkflow && !!currentWorkflow.team && item.initiatingUser) &&
          <div className="flex items-center w-60 flex-shrink-0 gap-2">
            {t("Ran by")}&nbsp;<span className="text-gray-600">{item.initiatingUser.fullName}</span>
          </div>
        }

        {/* Status */}
        <div className="flex items-center w-40 flex-shrink-0">
          {actionStatus === "INITIAL" &&
            <>
              {item.status === "PENDING" && <DotsCircleHorizontalIcon className="w-6 h-6 text-blue mx-auto" />}
              {item.status === "COMPLETED" && <CheckCircleIcon className="w-6 h-6 text-green mx-auto" />}
              {item.status === "COMPLETED_PARTIALLY" &&
                <>
                  {(item.stage === "REVIEW_PENDING" && (!currentWorkflow.team || item.approvalRequests.length === 0 || item.approvalRequests.filter(a => a.status === "PENDING" && a.approver.id === user?.id).length > 0))
                    ? <button className="btn btn-blue text-xs py-1 mx-auto" disabled={!hasPermission(currentWorkflow.team, "workflows.run")} onClick={(e) => { e.stopPropagation(); history.push(`/w/${item.workflow.id}/outputs/review?` + new URLSearchParams({ item: item.id }).toString()); }}>{t("Review")}</button>
                    : <DotsCircleHorizontalIcon className="w-6 h-6 text-blue mx-auto" />}
                </>
              }
              {item.status === "COMPLETED_WITH_ERROR" && <ExclamationCircleIcon className="w-6 h-6 text-yellow mx-auto" />}
            </>
          }

          {actionStatus === "RERUNNING" &&
            <div className="text-gray-400">
              {t("Rerunning...")}
            </div>
          }
        </div>

        {/* Created at */}
        <div className="flex items-center justify-center w-40 flex-shrink-0">
          {actionStatus === "DELETED" && t("Removed")}

          {actionStatus === "INITIAL" &&
            moment(props.item.createdAt).format("MMM D, h:mma")
          }
        </div>

        {/* Additional options (kebeb menu) */}
        <div className="flex justify-center items-center w-10 flex-shrink-0">
          {actionStatus === "INITIAL" &&
            <div className="flex justify-center relative">
              <Transition
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
                show={showCopiedMessage}
                unmount
              >
                <div className="absolute bg-blue whitespace-nowrap text-white rounded top-0 right-44 z-10 shadow px-2 -pb-1 pointer-events-none">
                  {t("Support code copied to clipboard")}
                </div>
              </Transition>
              <Popover className="relative" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <Popover.Button>
                  <DotsVerticalIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </Popover.Button>
                <Popover.Panel className="absolute mt-1 right-0 z-20 flex flex-col w-42 bg-white border rounded shadow overflow-y-auto">
                  {(currentWorkflow.source?.sourceType !== "SIGNATURE_LINK" && hasPermission(currentWorkflow.team, "workflows.run")) &&
                    <Popover.Button className="flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap" onClick={handleRerunItem}>
                      <RefreshIcon className="w-4 h-4 mr-2" /> {t("Rerun")}
                    </Popover.Button>
                  }
                  {hasPermission(currentWorkflow.team, "workflows.edit") && // TODO: Should this be workflows.run?
                    <Popover.Button className="flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap" onClick={handleDeleteItem}>
                      <TrashIcon className="w-4 h-4 mr-2" /> {t("Delete")}
                    </Popover.Button>
                  }
                  <CopyToClipboard text={item.id} onCopy={handleOnCopySupport}>
                    <Popover.Button className="flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap">
                      <ClipboardCopyIcon className="w-4 h-4 mr-2" /> {t("Copy Support Code")}
                    </Popover.Button>
                  </CopyToClipboard>
                  {(currentWorkflow.review && currentWorkflow.signatureConfig && item.stage === "SIGNATURES_PENDING") &&
                    <Popover.Button className="flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap" onClick={handleRescindItem}>
                      <BanIcon className="w-4 h-4 mr-2" /> {t("Cancel Signature Requests")}
                    </Popover.Button>
                  }
                </Popover.Panel>
              </Popover>
            </div>
          }
        </div>
      </div>

      {isExpanded &&
        <div className="p-4 mr-4 bg-gray-100 shadow-inner rounded">
          {(!primaryOutput && item.status === "PENDING") &&
            <div>
              <div className="pt-3 text-sm font-semibold text-gray-600">
                {t("This automation item is currently being completed...")}
              </div>
            </div>
          }

          {(!primaryOutput && item.status !== "PENDING") &&
            <>
              <div className="pt-3 text-sm text-gray-600">
                {t("An issue occured in the processing of this automation item, please re-run this item from the menu to the right.")}<br />
                {t("If the issue persists please contact support and provide the support code which you can copy from the same menu.")}

                {item.errorReason === "EXTEND_TABLES_ERROR" &&
                  <div className="mt-2 flex flex-col text-blue">
                    <div className="font-semibold">{t("Extend Tables Error")}</div>
                    <div>{t("The issue is most likely due to a table within your document that contains template tags in the same row as vertically merged cells.")}</div>
                    <div>{t("Please reformat this table to not use merged cells and try again.")}</div>
                  </div>
                }
              </div>
              <div className="flex flex-col h-full">
                <ArrowRightIcon className="w-6 h-6 text-gray-600 -rotate-45 animate-pulse mb-auto mt-2" />
              </div>
            </>
          }

          {documentOutputs.length > 0 &&
            <>
              <div className="font-semibold text-xs text-gray-400 pt-2">
                {t("Documents")}
              </div>
              {documentOutputs.map(output => <WorkflowOutputsItemFile key={output.file?.id + "-doc"} name={output.file!.name} url={output.file!.url} mimetype={output.file!.mimeType} status="Completed" />)}
            </>
          }

          {onedriveOuputs.length > 0 &&
            <>
              <div className="font-semibold text-xs text-gray-400 pt-2">
                {t("OneDrive Files")}
              </div>
              {documentOutputs.map(output => <WorkflowOutputsItemFile key={output.file?.id + "-onedrive"} name={output.onedriveFile!.fileName} url={output.onedriveFile!.fileUrl} mimetype={output.onedriveFile?.fileMimeType!} status="Completed" />)}
            </>
          }

          {(item.approvedBy && item.approvalRequests.length === 0) &&
            <>
              <div className="font-semibold text-xs text-gray-400 pt-2">
                {t("Review")}
              </div>

              <div className="flex items-center">
                <CheckCircleIcon className="w-4 h-4 mr-2 text-green" />
                <div className="">
                  {t("Reviewed by")} <span className="font-semibold text-gray-600">{item.approvedBy.fullName}</span>
                </div>

                <div className="text-sm text-center font-semibold text-green ml-auto w-40">
                  {t("Approved")}
                </div>

                <div className="text-sm text-center font-semibold text-gray-400 w-40 mr-2">
                  {moment(item.approvedAt).format("MMM D, h:mma")}
                </div>
              </div>
            </>
          }

          {approvalRequests.length > 0 &&
            <>
              <div className="font-semibold text-xs text-gray-400 pt-2">
                {t("Approval Requests")}
              </div>

              {approvalRequests.sort((a, b) => a.approverIndex - b.approverIndex).map((approvalRequest) =>
                <div key={approvalRequest.id} className="flex items-center">
                  <div className="flex items-center gap-2">
                    {approvalRequest.status === "PENDING" &&
                      <DocumentSearchIcon className="w-4 h-4 text-blue" />
                    }
                    {approvalRequest.status === "APPROVED" &&
                      <CheckCircleIcon className="w-4 h-4 text-green" />
                    }
                    {approvalRequest.status === "REJECTED" &&
                      <XCircleIcon className="w-4 h-4 text-red" />
                    }
                    <div className="text-gray-600">{approvalRequest.approver.fullName}</div>
                    <div className="text-gray-400 text-xs font-semibold mt-1">{approvalRequest.approver.email}</div>
                  </div>

                  <div className="text-sm text-center font-semibold ml-auto w-40">
                    {approvalRequest.status === "PENDING" &&
                      <div className="text-gray-400">{t("Requested")}</div>
                    }
                    {approvalRequest.status === "APPROVED" &&
                      <div className="text-green">{t("Approved")}</div>
                    }
                    {approvalRequest.status === "REJECTED" &&
                      <div className="text-red">{t("Rejected")}</div>
                    }
                  </div>
                  <div className="text-sm text-center font-semibold text-gray-400 w-40 mr-2">
                    {moment(approvalRequest.approvedAt).format("MMM D, h:mma")}
                  </div>
                </div>
              )}
            </>
          }

          {pdfOuputs.length > 0 &&
            <>
              <div className="font-semibold text-xs text-gray-400 pt-2">
                {t("PDF Files (Google Drive)")}
              </div>
              {pdfOuputs.map(output => <WorkflowOutputsItemFile key={output.filePdf?.id + "-pdf"} name={output.filePdf!.name} url={output.filePdf!.url} mimetype={"application/pdf"} status="Completed" />)}
            </>
          }

          {microsoftOuputs.length > 0 &&
            <>
              <div className="font-semibold text-xs text-gray-400 pt-2">
                {t("Microsoft Files (Google Drive)")}
              </div>
              {microsoftOuputs.map(output => <WorkflowOutputsItemFile key={output.fileMicrosoft?.id + "-ms"} name={output.fileMicrosoft!.name} url={output.fileMicrosoft!.url} mimetype={output.fileMicrosoft!.mimeType} status="Completed" />)}
            </>
          }

          {onedrivePdfs.length > 0 &&
            <>
              <div className="font-semibold text-xs text-gray-400 pt-2">
                {t("PDF Files (OneDrive)")}
              </div>
              {onedrivePdfs.map(output => <WorkflowOutputsItemFile key={output.onedrivePdf!.fileId + "-onedrive-pdf"} name={output.onedrivePdf!.fileName} url={output.onedrivePdf!.fileUrl} mimetype={"application/pdf"} status="Completed" />)}
            </>
          }

          {hubspotPdfs.length > 0 &&
            <>
              <div className="font-semibold text-xs text-gray-400 pt-2">
                {t("PDF Files (HubSpot)")}
              </div>
              {hubspotPdfs.map(output => <WorkflowOutputsItemFile key={output.hubspotFilePdf!.fileId + "-hubspot-pdf"} name={output.hubspotFilePdf!.fileName} url={output.hubspotFilePdf!.fileUrl} mimetype={"application/pdf"} status="Completed" />)}
            </>
          }

          {hubspotMicrosftFiles.length > 0 &&
            <>
              <div className="font-semibold text-xs text-gray-400 pt-2">
                {t("Microsoft Files (HubSpot)")}
              </div>
              {/* TODO: May be wrong if the file type is actually powerpoint */}
              {hubspotMicrosftFiles.map(output => <WorkflowOutputsItemFile key={output.hubspotFileMicrosoft!.fileId + "-hubspot-ms"} name={output.hubspotFileMicrosoft!.fileName} url={output.hubspotFileMicrosoft!.fileUrl} mimetype={"application/vnd.openxmlformats-officedocument.wordprocessingml.document"} status="Completed" />)}
            </>
          }

          {signatureRequests.length > 0 &&
            <>
              <div className="font-semibold text-xs text-gray-400 pt-2">
                {t("Signature Requests")}
              </div>

              {signatureRequests.sort((a, b) => a.recipientNumber - b.recipientNumber).map((signatureRequest) =>
                <div className="flex flex-col gap-1">
                  <div key={signatureRequest.id} className="flex items-center">

                    <div className="flex items-center gap-2">
                      {signatureRequest.recipientNumber === 0 &&
                        <DocumentTextIcon className="w-4 h-4 text-blue" />
                      }
                      {signatureRequest.recipientNumber > 0 &&
                        <>
                          {signatureRequest.status === "LINK" &&
                            <LinkIcon className="w-4 h-4 text-blue" />
                          }
                          {signatureRequest.status === "PENDING" &&
                            <DotsHorizontalIcon className="w-4 h-4 text-blue" />
                          }
                          {signatureRequest.status === "REQUESTED" &&
                            <MailIcon className="w-4 h-4 text-blue" />
                          }
                          {signatureRequest.status === "ACCESSED" &&
                            <MailOpenIcon className="w-4 h-4 text-blue" />
                          }
                          {signatureRequest.status === "COMPLETED" &&
                            <BadgeCheckIcon className="w-4 h-4 text-blue" />
                          }
                          {signatureRequest.status === "ERROR" &&
                            <ExclamationIcon className="w-4 h-4 text-red" />
                          }
                          {["CANCELED", "EXPIRED"].includes(signatureRequest.status) &&
                            <BanIcon className="w-4 h-4 text-yellow" />
                          }
                        </>
                      }
                      {(signatureRequest.recipientNumber === 0 || signatureRequest.status === "LINK" || signatureRequest.status === "LINK_UNPUBLISHED")
                        ? <div className="flex items-baseline gap-1 relative text-gray-600 hover:underline hover:text-blue cursor-pointer" onClick={() => window.open(signatureRequest.publicTokenUrl, "_blank")}>{t("Preview Signable Document")} <ExternalLinkIcon className="w-4 h-4 flex-shrink-0 top-0.5 relative" /></div>
                        : (!!signatureRequest.recipientEmail
                          ? <div className="text-gray-600 w-80 truncate">{signatureRequest.recipientEmail}</div>
                          : <div className="text-gray-400 w-80 truncate">{t("No email has been provided")}</div>
                        )
                      }
                      {(["REQUESTED", "ACCESSED"].includes(signatureRequest.status) && signatureRequest.expiry) &&
                        <div className="text-xs text-gray-600 flex items-center gap-1 ml-4 w-48 truncate">
                          <ClockIcon className="w-4 h-4" />
                          {t("Expires")} {moment(signatureRequest.expiry).fromNow()}
                        </div>
                      }
                      {(signatureRequest.recipientNumber > 0 && (signatureRequest.status === "REQUESTED" || signatureRequest.status === "ACCESSED")) &&
                        <div className="flex items-center ml-4">
                          <div className="text-xs text-gray-600">{t("Signature Request Link")}:</div>
                          <a className="flex items-center gap-1 text-xs text-gray-600 ml-2 hover:underline cursor-pointer -mt-1" target={signatureRequest.publicTokenUrl} href={signatureRequest.publicTokenUrl} rel="noreferrer">
                            <ExternalLinkIcon className="w-4 h-4 flex-shrink-0" /> {t("Open")}
                          </a>
                          <CopyToClipboard text={signatureRequest.publicTokenUrl} onCopy={() => handleOnCopiedSignatureRequest(signatureRequest.recipientNumber)}>
                            {copiedSignatureRequest === signatureRequest.recipientNumber
                              ? <div className="flex items-center gap-1 text-xs text-blue ml-2 font-semibold select-none -mt-1"><ClipboardCopyIcon className="w-4 h-4 flex-shrink-0" /> {t("Copied")}</div>
                              : <div className="flex items-center gap-1 text-xs text-gray-600 ml-2 hover:underline cursor-pointer -mt-1"><ClipboardCopyIcon className="w-4 h-4 flex-shrink-0" /> {t("Copy")}</div>
                            }
                          </CopyToClipboard>
                        </div>
                      }
                    </div>

                    <div className="text-sm text-center font-semibold ml-auto w-40">
                      {signatureRequest.recipientNumber > 0 &&
                        <>
                          {signatureRequest.status === 'PENDING' &&
                            <div className="text-gray-400">{t("Pending...")}</div>
                          }
                          {signatureRequest.status === 'REQUESTED' &&
                            <div className="text-blue">{t("Requested")}</div>
                          }
                          {signatureRequest.status === 'ACCESSED' &&
                            <div className="text-blue">{t("Accessed")}</div>
                          }
                          {signatureRequest.status === 'COMPLETED' &&
                            <div className="text-green">{t("Completed")}</div>
                          }
                          {signatureRequest.status === 'ERROR' &&
                            <div className="text-yellow">{t("Failed")}</div>
                          }
                          {signatureRequest.status === 'CANCELED' &&
                            <div className="text-yellow">{t("Canceled")}</div>
                          }
                        </>
                      }
                    </div>
                    <div className="flex justify-center text-sm font-semibold text-gray-400 w-40 mr-2">
                      {(signatureRequest.status === "REQUESTED" || signatureRequest.status === "ACCESSED") &&
                        <>
                          {resendStatus === "INITIAL" &&
                            <button className="btn btn-blue text-xs h-6 w-32" onClick={(e) => { e.stopPropagation(); handleResendSignatureRequest(signatureRequest.id); }}>{t("Resend")}</button>
                          }
                          {resendStatus === "SENDING" &&
                            <button className="btn btn-blue text-xs h-6 w-32" disabled>{t("Sending...")}</button>
                          }
                          {resendStatus === "RESENT" &&
                            <button className="btn btn-green text-xs cursor-not-allowed h-6 w-32">{t("Resent")}</button>
                          }
                          {resendStatus === "ERROR" &&
                            <button className="btn btn-red text-xs cursor-not-allowed h-6 w-32">{t("Error")}</button>
                          }
                        </>
                      }
                    </div>
                  </div>
                  <div className="flex flex-col">
                    {signatureRequest.trackingPixel?.events.map((event: TrackingPixelEvent) =>
                      <div key={event.id} className="text-xs text-gray-600 flex items-center gap-1 ml-6">
                        <EyeIcon className="w-4 h-4 flex-shrink-0" /> {moment(event.createdAt).format("MMM D, h:mma")} ({moment(event.createdAt).fromNow()})
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          }


          {outputEmails.length > 0 &&
            <>
              <div className="font-semibold text-xs text-gray-400 pt-2">
                {t("Emails")}
              </div>

              {outputEmails.map((email) =>
                <div key={email.id} className="flex items-center">
                  <div>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <img className="w-4 h-4" src={BLOCK_ICON_MAP[email.emailType]} alt="Email icon" />
                        <a className="group hover:underline hover:text-blue" href={email.emailType === "GMAIL" ? "https://mail.google.com/mail/u/0/#sent/" + email.messageId : "https://outlook.office365.com/mail/sentitems"} target="_blank" rel="noreferrer">
                          {email.emailSubject}<ExternalLinkIcon className="hidden group-hover:inline-block w-4 h-4 ml-1 text-blue" />
                        </a>
                      </div>
                      <div className={`ml-4 text-sm flex items-center gap-2 ${email.errorField === "email_to" ? "text-yellow font-semibold" : "text-black"}`}>
                        {email.errorField === "email_to"
                          ? <ExclamationIcon className="text-yellow flex-shrink-0 w-4 h-4" />
                          : <ExclamationIcon className="opacity-0 flex-shrink-0 w-4 h-4" />
                        }
                        <span className="font-semibold text-gray-600">To:</span> {email.emailTo || <span className={`font-semibold ${email.errorField === "email_to" ? "text-yellow" : "text-gray-400"}`}>{"<blank>"}</span>}
                      </div>
                      {email.emailCc &&
                        <div className={`ml-4 text-sm flex items-center gap-2 ${email.errorField === "email_cc" ? "text-yellow font-semibold" : "text-black"}`}>
                          {email.errorField === "email_cc"
                            ? <ExclamationIcon className="text-yellow flex-shrink-0 w-4 h-4" />
                            : <ExclamationIcon className="opacity-0 flex-shrink-0 w-4 h-4" />
                          }
                          <span className="font-semibold text-gray-600">Cc:</span> {email.emailCc}
                        </div>
                      }
                      {email.emailBcc &&
                        <div className={`ml-4 text-sm flex items-center gap-2 ${email.errorField === "email_bcc" ? "text-yellow font-semibold" : "text-black"}`}>
                          {email.errorField === "email_bcc"
                            ? <ExclamationIcon className="text-yellow flex-shrink-0 w-4 h-4" />
                            : <ExclamationIcon className="opacity-0 flex-shrink-0 w-4 h-4" />
                          }
                          <span className="font-semibold text-gray-600">Bcc:</span> {email.emailBcc}
                        </div>
                      }
                      {(email.status === "FAILED" && email.errorField === "__rate_limit") &&
                        <div className="ml-4 text-sm flex items-center gap-2">
                          <ExclamationIcon className="text-yellow flex-shrink-0 w-4 h-4" />
                          <span className="text-yellow font-semibold">{t("Rate limit exceeded, please try again later")}</span>
                        </div>
                      }
                      {(email.status === "FAILED" && !email.errorField) &&
                        <div className="ml-4 text-sm flex items-center gap-2">
                          <ExclamationIcon className="text-yellow flex-shrink-0 w-4 h-4" />
                          <span className="text-yellow font-semibold">{t("An unknown error occured")}</span>
                        </div>
                      }
                    </div>
                    {email.trackingPixel?.events.map((event: TrackingPixelEvent) =>
                      <div key={event.id} className="text-xs text-gray-600 flex items-center gap-1 ml-6">
                        <EyeIcon className="w-4 h-4 flex-shrink-0" /> {moment(event.createdAt).format("MMM D, h:mma")} ({moment(event.createdAt).fromNow()})
                      </div>
                    )}
                  </div>

                  <div className="text-sm text-center font-semibold text-green ml-auto w-40">
                    {email.status === 'PENDING' &&
                      <div>{t("Pending...")}</div>
                    }
                    {email.status === 'SENT' &&
                      <div className="text-green">{t("Sent")}</div>
                    }
                    {email.status === 'FAILED' &&
                      <div className="text-yellow">{t("Failed")}</div>
                    }
                  </div>

                  <div className="flex justify-center text-sm font-semibold text-gray-400 w-40 mr-2" />
                </div>
              )}
            </>
          }

          {item.stripePaymentLink &&
            <>
              <div className="font-semibold text-xs text-gray-400 pt-2">
                {t("Stripe Payment Link")}
              </div>
              {[{ id: "0" }].map((data: { id: string }) =>
                <div key={data.id} className="flex items-center">
                  <div className="flex items-center gap-2">
                    <img className="h-4 w-4 rounded" src=" https://static.portant.co/stripe-logo.png" alt="Stripe" />
                    {t("A payment link was created for this automation item")}
                  </div>
                  <div className="text-sm text-center font-semibold ml-4">
                    {/* TODO: This is hacky as it uses the signature request state, improve this */}
                    <CopyToClipboard text={item.stripePaymentLink!.url} onCopy={() => handleOnCopiedSignatureRequest(0)}>
                      {copiedSignatureRequest === 0
                        ? <div className="flex items-center gap-1 text-xs text-blue font-semibold select-none">{t("Payment link copied to clipboard")}</div>
                        : <div className="flex items-center gap-1 text-xs text-gray-600 ml-5 hover:underline cursor-pointer"><ClipboardCopyIcon className="w-4 h-4 flex-shrink-0" />{t("Copy payment link")}</div>
                      }
                    </CopyToClipboard>
                  </div>
                </div>
              )}
            </>
          }
        </div>
      }
    </>
  );
}

export default WorkflowOutputsItem;
